import { Spinner } from 'components/spinner/Spinner';
import {
    FileLibraryObjectParams,
    useFileLibraryObject,
} from 'custom_hooks/data_hooks/file/useFileLibraryObject';
import * as React from 'react';
import { scalingStyles } from './shared';

type ImageProps = React.DetailedHTMLProps<
    React.VideoHTMLAttributes<HTMLVideoElement>,
    HTMLVideoElement
>;

export type AuthorizedVideoProps = Pick<ImageProps, 'className' | 'aria-label'> & {
    /**
     * Displayed while the image is being loaded.
     */
    pending?: React.ReactNode;
    /**
     * Displayed when there is an error fetching the image or the image source is undefined. Defaults to nothing.
     */
    fallback?: React.ReactNode;

    /**
     * How to scale the image ('object-fit' css property)
     */
    imageScaling?: keyof typeof scalingStyles;
} & FileLibraryObjectParams;

export function AuthorizedVideo({
    pending,
    fallback,
    className,
    'aria-label': ariaLabel,
    imageScaling,
    ...idProps
}: AuthorizedVideoProps) {
    const {
        data: video,
        error,
        isLoading: isVideoPending,
    } = useFileLibraryObject(idProps);

    if (isVideoPending) {
        return pending ? (
            <div
                className={className}
                style={imageScaling && scalingStyles[imageScaling]}
            >
                {pending}
            </div>
        ) : (
            <Spinner />
        );
    }

    if (error || !video) {
        return fallback ? (
            <div
                className={className}
                style={imageScaling && scalingStyles[imageScaling]}
            >
                {fallback}
            </div>
        ) : (
            <></>
        );
    }

    return (
        // we simply do not have this information, and it is usually not
        // relevant for our videos anyways
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video
            controls
            className={className}
            aria-label={ariaLabel}
            style={imageScaling && scalingStyles[imageScaling]}
        >
            <source src={video.blobUrl} />
        </video>
    );
}
